import React from 'react';
import PropTypes from 'prop-types';
import Popup from '../../../02_molecules/Popup';
import CookieConsentDialog from '../../../02_molecules/CookieConsentDialog';
import Button from '../../../01_atoms/Button';
import FormattedText from '../../../01_atoms/FormattedText';

import styles from './withCookieConsentSoundCloud.module.scss';

/* global Cookiebot */
const withCookieConsentSoundCloud = (SoundCloudBlock) => {
  class SoundCloudBlockWithCookieConsent extends React.Component {
    constructor(props) {
      super(props);

      this.state = {
        isCookiebotLoaded: false,
        isComponentVisible: false,
        isDialogVisible: false,
        isSubmitted: false,
      };

      this.onCookiebotLoad = this.onCookiebotLoad.bind(this);
      this.onAcceptCookie = this.onAcceptCookie.bind(this);
      this.onCancelDialog = this.onCancelDialog.bind(this);
    }

    componentDidMount() {
      const { cookieConsentType } = this.props;
      if (cookieConsentType) {
        window.addEventListener('CookiebotOnConsentReady', this.onCookiebotLoad);
      }

      // In case when Cookibot already loaded we need to switch component to
      // the right state. For example when we open popup with embedded video.
      if (typeof Cookiebot !== 'undefined') {
        this.onCookiebotLoad();
      }
    }

    componentWillUnmount() {
      const { cookieConsentType } = this.props;
      if (cookieConsentType) {
        window.removeEventListener('CookiebotOnConsentReady', this.onCookiebotLoad);
      }
    }

    onCookiebotLoad() {
      const { cookieConsentType } = this.props;

      if (Cookiebot && Cookiebot.consent && Cookiebot.consent[cookieConsentType]) {
        this.setState({
          isCookiebotLoaded: true,
          isComponentVisible: true,
          isDialogVisible: false,
        });
      } else {
        this.setState({
          isCookiebotLoaded: true,
          isComponentVisible: false,
          isDialogVisible: false,
        });
      }
    }

    onAcceptCookie(preferences, statistics, marketing) {
      const { isExample } = this.props;
      if (isExample) {
        this.setState({ isCookiebotLoaded: true, isComponentVisible: true, isSubmitted: true });
      } else {
        Cookiebot.submitCustomConsent(preferences, statistics, marketing);
        this.setState({ isSubmitted: true });
      }
    }

    onCancelDialog() {
      this.setState({ isDialogVisible: false });
    }

    render() {
      const {
        isExample,
        cookieConsentType,
        cookieDialogHeading,
        cookieDialogDescription,
        cookieConsentPreviewTitle,
        cookieConsentPreviewHeading,
        html,
      } = this.props;
      const { isCookiebotLoaded, isComponentVisible, isDialogVisible, isSubmitted } = this.state;

      // We expect that cookieConsentType will be always defined. Otherwise
      // we think that component doesn't expect certain level of cookies, so
      // we can render component as is.
      if (!cookieConsentType && !isExample) {
        return <SoundCloudBlock {...this.props} />;
      }

      let preferences;
      let statistics;
      let marketing;

      if (isCookiebotLoaded || isExample) {
        if (isComponentVisible) {
          let newHtml = html;
          if (isSubmitted) {
            newHtml = newHtml.replace('auto_play=false', 'auto_play=true');
          }

          return <SoundCloudBlock {...this.props} html={newHtml} />;
        }

        if (!isExample) {
          preferences = cookieConsentType === 'preferences';
          statistics = cookieConsentType === 'statistics';
          marketing = cookieConsentType === 'marketing';

          if (Cookiebot && Cookiebot.consent) {
            preferences = preferences || Cookiebot.consent.preferences;
            statistics = statistics || Cookiebot.consent.statistics;
            marketing = marketing || Cookiebot.consent.marketing;
          }
        }
      }

      const title =
        cookieDialogHeading || `Please accept ${cookieConsentType} cookies to view this content.`;

      return (
        <div className={`bb-soundcloud-with-cookie ${styles['bb-soundcloud-with-cookie']}`}>
          <div
            className="soundcloud-preview"
            onClick={() => this.setState({ isDialogVisible: true })}
            onKeyPress={() => this.setState({ isDialogVisible: true })}
          >
            <div className="soundcloud-preview-header">
              {cookieConsentPreviewHeading && <div>{cookieConsentPreviewHeading}</div>}
              {cookieConsentPreviewTitle && <span>{cookieConsentPreviewTitle}</span>}
            </div>
            <div className="soundcloud-preview-body">
              <div className="play-button" />
              <div className="soundcloud-audiograph" />
            </div>
          </div>

          {isDialogVisible && (
            <>
              <Popup
                className="popup-cookie-consent-dialog d-md-none"
                isVisible={isDialogVisible}
                onPopupClose={() => this.onCancelDialog()}
              >
                <CookieConsentDialog
                  className="cookie-consent-context-dialog"
                  logo={{ url: '/static/icons/social/soundcloud.svg', alt: 'soundcloud logo' }}
                  title={title}
                  description={cookieDialogDescription}
                  onAccept={() => this.onAcceptCookie(preferences, statistics, marketing)}
                  onDecline={() => this.onCancelDialog()}
                  buttonClassName="allow-cookies-soundcloud-bb"
                />
              </Popup>

              <div className="cookie-consent-soundcloud-dialog-desktop d-none d-md-flex">
                <div>
                  <div className="header">
                    <div className="logo" />
                    <h4 className="title">{title}</h4>
                  </div>
                  {cookieDialogDescription && (
                    <FormattedText className="description" text={cookieDialogDescription} />
                  )}
                </div>
                <div className="buttons">
                  <Button
                    className="allow-cookies-soundcloud-bb"
                    type="secondary"
                    onClick={() => this.onAcceptCookie(preferences, statistics, marketing)}
                  >
                    Allow cookies
                  </Button>
                  <div
                    className="link ignore-button"
                    onClick={() => this.onCancelDialog()}
                    onKeyPress={() => this.onCancelDialog()}
                  >
                    Ignore
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      );
    }
  }

  SoundCloudBlockWithCookieConsent.propTypes = {
    // isExample param can be used in case when we have to gain
    // the same behaviour of the component without Cookiebot.
    // Already used on the Body Blocks page in styleguide.
    isExample: PropTypes.bool,
    cookieConsentType: PropTypes.string,
    cookieDialogHeading: PropTypes.string,
    cookieDialogDescription: PropTypes.string,
    cookieConsentPreviewTitle: PropTypes.string,
    cookieConsentPreviewHeading: PropTypes.string,
    html: PropTypes.string.isRequired,
  };

  SoundCloudBlockWithCookieConsent.defaultProps = {
    isExample: false,
    cookieConsentType: '',
    cookieDialogHeading: '',
    cookieDialogDescription: '',
    cookieConsentPreviewTitle: '',
    cookieConsentPreviewHeading: '',
  };

  return SoundCloudBlockWithCookieConsent;
};

export default withCookieConsentSoundCloud;
